// Rücksprung in das aufrufende Werkzeug (Ticket 2026-08-26).
//
// Bisher landete jede Anmeldung hart auf dem Dashboard. Werkzeuge, die den
// Nutzer zum Portal schicken, um eine Anmeldung zu holen, kamen deshalb nie
// zurück - z. B. die Anmelde-Brücke des Kalkulationstools, wenn die Ansicht
// eingebettet in Salesforce läuft (dort ist der Speicher partitioniert, die
// Anmeldung muss übernommen werden), oder der Sitzungsablauf in den Tools, die
// schon heute `?returnTo=…` mitschicken.
//
// Ablauf: `captureReturnTo()` sichert den Parameter, BEVOR der Auth0-Sprung die
// Adresse verliert (Auth0 kehrt auf die nackte Origin zurück). Nach der
// Anmeldung liest `consumeReturnTo()` ihn wieder und `buildToolUrl()` hängt
// dieselben Parameter an wie der Kachel-Klick im Dashboard.
//
// ⚠️ Nur eigene Domänen (*.muessig.app): Das Ziel bekommt das Anmeldetoken in
// der Adresse - eine offene Weiterleitung wäre hier eine Tokenausgabe an
// beliebige Fremdseiten.

const STORAGE_KEY = 'mu_return_to';

// Eigene Werkzeuge, die nicht unter muessig.app erreichbar sind, sondern direkt
// unter ihrer Firebase- oder App-Engine-Adresse.
//
// ⚠️ Bewusst vollständige Hostnamen. Eine Endungsregel auf `.web.app` oder
// `.appspot.com` würde jede fremde Seite auf diesen geteilten Domänen
// einschliessen und ihr das Anmeldetoken aushändigen.
//
// Quellen: Tabelle `Site` (Werkzeugliste des Portals, inklusive
// `alternativeUrl`) und `OriginPolicyRegistry` im microservice-j11 (dort stehen
// zusätzlich die Firebase-Standardadressen, unter denen dieselben Werkzeuge
// erreichbar sind). Hosts unter `*.muessig.app` fehlen hier absichtlich - die
// deckt die Regel darunter schon ab.
//
// NICHT aufnehmen: `www.muessig.ch` (Firmenseite) und `sites.google.com`
// (Wissensdatenbank). Beide stehen in `Site`, sind aber keine eigenen
// Werkzeuge und brauchen kein Anmeldetoken.
const ADDITIONAL_ALLOWED_HOSTS = [
  // Firebase-Adressen unserer Werkzeuge
  'bestellportal-dev.web.app',
  // Projekt-Manager und Einschreiben verlinken produktiv auf diese Adresse
  'bestellportal-test.web.app',
  'calculation-e7947.web.app',
  'calculation-test-693d1.web.app',
  'deadline-chaser.web.app',
  'einschreiben-prod.web.app',
  'invoice-test-6f283.web.app',
  'montage-app-v3.web.app',
  'network-plan-3cd45.web.app',
  'network-plan-test.web.app',
  'planning-board-constr-test.web.app',
  'planning-board-construction.web.app',
  'planningboard-dev.web.app',
  'planningboard-prod.web.app',
  'project-manager-msg-test.web.app',
  'project-manager-msg.web.app',
  'regietool.web.app',
  'section-parser-dev.web.app',
  'siaparser.web.app',
  'stueckliste-dev.web.app',
  'stueckliste-test.web.app',
  // Ältere App-Engine-Adressen (GCP-Projekt dev-splicer-204213), teils noch
  // als Haupt- oder Ersatzadresse in `Site` eingetragen
  'invoice-dot-dev-splicer-204213.ey.r.appspot.com',
  'plantafel-dot-dev-splicer-204213.ey.r.appspot.com',
  'projectmanager-dot-dev-splicer-204213.ey.r.appspot.com',
  'stueckliste-dot-dev-splicer-204213.ey.r.appspot.com'
];

function isAllowedTarget(url: URL): boolean {
  const host = url.hostname.toLowerCase();
  const local = /^(localhost|127\.0\.0\.1)$/.test(host);

  // Lokale Ziele nur, wenn das Portal selbst lokal läuft (Entwicklung).
  if (local) {
    return /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname.toLowerCase());
  }
  if (url.protocol !== 'https:') return false;
  if (host === 'muessig.app' || host.endsWith('.muessig.app')) return true;
  return ADDITIONAL_ALLOWED_HOSTS.indexOf(host) !== -1;
}

/** Prüft `returnTo` und gibt die normalisierte Adresse zurück (sonst null). */
export function safeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return isAllowedTarget(url) ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * Merkt sich ein `returnTo` aus der aktuellen Adresse.
 *
 * Muss vor dem Routing laufen (AppComponent-Konstruktor): der Router leitet ''
 * auf '/login' um und der Auth0-Sprung kehrt ohne Suchparameter zurück.
 */
export function captureReturnTo(): void {
  try {
    const raw = new URLSearchParams(window.location.search).get('returnTo');
    const target = safeReturnTo(raw);
    if (target) sessionStorage.setItem(STORAGE_KEY, target);
  } catch {
    /* Speicher gesperrt - dann bleibt es beim Dashboard. */
  }
}

/** Liest das gemerkte Ziel und verbraucht es (nur ein Sprung). */
export function consumeReturnTo(): string | null {
  try {
    const target = safeReturnTo(sessionStorage.getItem(STORAGE_KEY));
    sessionStorage.removeItem(STORAGE_KEY);
    return target;
  } catch {
    return null;
  }
}

/**
 * Hängt die Anmeldeparameter an - identisch zu `siteUrl()` im Dashboard, damit
 * die Werkzeuge nichts Neues lernen müssen (`user`, `token` = JWT,
 * `tstoken` = statisches Token).
 */
export function buildToolUrl(
  target: string,
  auth: { user?: string; jwt?: string; tsToken?: string }
): string {
  const url = new URL(target);
  if (auth.user) url.searchParams.set('user', auth.user);
  if (auth.jwt) url.searchParams.set('token', auth.jwt);
  if (auth.tsToken) url.searchParams.set('tstoken', auth.tsToken);
  return url.toString();
}
